import {
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  Alert,
  Snackbar,
  Autocomplete,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { RHFTextField } from 'src/components/hook-form';
import RHFMarkdownEditor from 'src/components/markdown-editor/markdownEditor';
import RHFImageUpload from 'src/components/hook-form/rhf-image-upload';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { addTeleHealthPackage, getTeleHealthTherapyCategories } from 'src/services/telehealth.service';
import { searchConcerns } from 'src/services/concerns.service';
import { searchProducts } from 'src/services/products.service';
import { getBodyParts } from '../../../services/treatments.service';

export default function ClinicAddTeleHealthView() {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [concernOptions, setConcernOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [therapyCategories, setTherapyCategories] = useState([]);
  const [bodyPartsOptions, setBodyPartsOptions] = useState([]);

  const [concernInput, setConcernInput] = useState('');
  const [productInput, setProductInput] = useState('');

  const concernDebounce = useRef(null);
  const productDebounce = useRef(null);

  useEffect(() => {
    getTeleHealthTherapyCategories().then((res) =>
      setTherapyCategories((res || []).map((c) => ({ value: c.value, label: c.label })))
    );
    getBodyParts().then((res) => setBodyPartsOptions(res || []));
  }, []);

  useEffect(() => {
    if (concernDebounce.current) clearTimeout(concernDebounce.current);
    if (concernInput && concernInput.length >= 2) {
      concernDebounce.current = setTimeout(() => {
        searchConcerns(concernInput).then((res) => setConcernOptions(res || []));
      }, 300);
    }
    return () => concernDebounce.current && clearTimeout(concernDebounce.current);
  }, [concernInput]);

  useEffect(() => {
    if (productDebounce.current) clearTimeout(productDebounce.current);
    if (productInput && productInput.length >= 2) {
      productDebounce.current = setTimeout(() => {
        searchProducts(productInput).then((res) => setProductOptions(res || []));
      }, 300);
    }
    return () => productDebounce.current && clearTimeout(productDebounce.current);
  }, [productInput]);

  const schema = Yup.object().shape({
    therapyCategory: Yup.number().required(),
    therapy_name: Yup.string().required(),
    description: Yup.string(),
    how_it_works: Yup.string(),
    expected_outcomes: Yup.array().of(Yup.string()),
    min_duration_months: Yup.number().min(1).required(),
    max_duration_months: Yup.number().min(1).required(),
    body_parts: Yup.array().of(Yup.string()),
    concern_ids: Yup.array().of(Yup.string()),
    products: Yup.array().of(Yup.string()),
    prescription_required: Yup.boolean(),
    therapy_images: Yup.array().of(Yup.string()),
    promo_image: Yup.string().nullable(),
  });

  const defaultValues = {
    therapyCategory: '',
    therapy_name: '',
    description: '',
    how_it_works: '',
    expected_outcomes: [],
    min_duration_months: 1,
    max_duration_months: 1,
    body_parts: [],
    concern_ids: [],
    products: [],
    prescription_required: false,
    therapy_images: [],
    promo_image: '',
  };

  const methods = useForm({ resolver: yupResolver(schema), defaultValues });
  const { control, watch, setValue, handleSubmit, reset } = methods;

  const promoImage = watch('promo_image');
  const therapyImages = watch('therapy_images');

  const onSubmit = (data) => {
    console.log(data);
    addTeleHealthPackage({ ...data, clinic_id: user.clinic_id })
      .then(() => {

        reset();
        setSnackbarMessage('Tele health therapy created successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        navigate(-1);
      })
      .catch((err) => {
        setSnackbarMessage(err.message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };

  return (
    <Container sx={{ background: { lg: 'white' }, margin: 4, padding: 3, maxWidth: 840, borderRadius: 4 }}>
      <Stack direction="row" gap="14px" alignItems="center" sx={{ marginBottom: '14px' }}>
        <IconButton size="large" onClick={() => navigate(-1)}>
          <Iconify icon="tabler:chevron-left" />
        </IconButton>
        <Typography variant="h4">Create Tele Health Therapy</Typography>
      </Stack>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={{ xs: '14px', lg: '44px' }}>
            <Grid item xs={12} lg={6}>
              <RHFTextField name="therapy_name" label="Therapy Name" required margin="dense" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Controller
                name="therapyCategory"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={therapyCategories}
                    getOptionLabel={(o) => o?.label || ''}
                    value={therapyCategories.find((c) => c.value === field.value) || null}
                    onChange={(_, v) => field.onChange(v?.value || '')}
                    renderInput={(params) => <TextField {...params} label="Therapy Category" margin="dense" />}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <RHFMarkdownEditor name="description" rows={4} fullWidth />
            </Grid>

            <Grid item xs={12}>
              <RHFMarkdownEditor name="how_it_works" rows={3} fullWidth />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField name="min_duration_months" type="number" label="Min Duration (Months)" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField name="max_duration_months" type="number" label="Max Duration (Months)" />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="expected_outcomes"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value || []}
                    onChange={(_, v) => field.onChange(v)}
                    renderTags={(v, getTagProps) =>
                      v.map((o, i) => <Chip label={o} {...getTagProps({ index: i })} />)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Expected Outcomes" margin="dense" />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Controller
                name="body_parts"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={bodyPartsOptions}
                    getOptionLabel={(o) => o}
                    value={field.value || []}
                    onChange={(_, v) => field.onChange(v)}
                    renderInput={(params) => <TextField {...params} label="Body Parts" margin="dense" />}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Controller
                name="concern_ids"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={concernOptions}
                    getOptionLabel={(o) => o?.name || ''}
                    filterOptions={(x) => x}
                    inputValue={concernInput}
                    onInputChange={(_, v) => setConcernInput(v)}
                    onChange={(_, v) => field.onChange(v.map((x) => x.id))}
                    renderInput={(params) => <TextField {...params} label="Concerns" margin="dense" />}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="products"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={productOptions}
                    getOptionLabel={(o) => o?.product_name || ''}
                    filterOptions={(x) => x}
                    inputValue={productInput}
                    onInputChange={(_, v) => setProductInput(v)}
                    onChange={(_, v) => field.onChange(v.map((x) => x.id))}
                    renderInput={(params) => <TextField {...params} label="Products" margin="dense" />}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFImageUpload
                initialImage={promoImage}
                name="promo_image"
                setValue={setValue}
                folderName={`images/telehealth/${user.clinic_id}`}
                label="Promo Image"
                maxFiles={1}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFImageUpload
                initialImage={therapyImages}
                name="therapy_images"
                setValue={setValue}
                folderName={`images/telehealth/${user.clinic_id}/gallery`}
                label="Therapy Images"
                maxFiles={10}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Controller
                    name="prescription_required"
                    control={control}
                    render={({ field }) => <Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
                  />
                }
                label="Prescription Required"
              />
            </Grid>

            <Grid item xs={12}>
              <Button type="submit" variant="contained" fullWidth sx={{ boxShadow: 10, background: '#D92D7F', ':hover': { background: '#D92D7F' } }}>
                Save Tele Health Therapy
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}
